import { AppStartupStatus } from "app";
import { Cursor } from "components/actions/cursor";
import { AppModule } from "components/appModules/appModule";
import appModuleRegistry from "components/appModules/appModuleRegistry";
import { LocalStorage } from "components/appModules/LocalStorage";
import i18n from "components/i18n/i18n";
import * as message from "components/message/message";
import { Renderer } from "components/Renderer";
import sislogUserGlobals from "components/UserGlobals";
import $ from "jquery";
import Navigo from "navigo";
import { IndexHeader } from "pages/index/IndexHeader";
import { DiscardConfirmation } from "./discardConfirmation";
import { LdsRouter } from "./LdsRouter";
import { PreventBrowseOut } from "./preventBrowseOut";

export class Router {
    private static instance: Router;
    private static readonly root = null;
    private static readonly useHash = true; // Defaults to: false
    private static readonly hash = "#!"; // Defaults to: '#'
    private readonly router = new Navigo(Router.root, Router.useHash, Router.hash);
    private static appStartupStatus: AppStartupStatus;
    private legacyCallbackReference: any;
    private controller: AbortController;
    private signal: AbortSignal;

    private prototypeHtmlElementFunctions = {};

    public static getInstance(appStartupStatus?: AppStartupStatus) {
        return this.instance || (this.instance = new this(appStartupStatus));
    }

    private constructor(appStartupStatus: AppStartupStatus) {
        this.controller = new AbortController();
        this.signal = this.controller.signal;
        const dc = new DiscardConfirmation();
        const pbo = new PreventBrowseOut();
        Router.appStartupStatus = appStartupStatus;
        this.router.on(() => {
            this.router.navigate("/dashboard");
        });
        this.createJsonRoute(this.router, "/index/index");
        LdsRouter.createLdsRoute(this.router, this.loadBody);

        this.router.hooks({
            before: (done) => {
                this.controller.abort();
                this.controller = new AbortController();
                this.signal = this.controller.signal;
                if (this.legacyCallbackReference != null) {
                    window.removeEventListener("s5-base-legacy-scripts-loaded", this.legacyCallbackReference);
                }
                done();
            },
        });
        this.router
            .on("/changeLanguage/:lng", this.changeLanguageRouter)
            .on("/logout", this.logout)
            .on("/login", this.loginRouter)
            .on("/expiredPassword", this.expiredPasswordRouter)
            .on("/dashboard", this.dashboardRouter,
                {
                    before: (done, params) => {
                        done();
                        appModuleRegistry.currentApp = null;
                        sislogUserGlobals.appEnvironment = {};
                        IndexHeader.instance.updateHeaderForApp("dashboard");
                    },
                },
            )
            .on("/user-details", this.userDetailsRouter)
            .on("/main/:page", this.mainRouter)
            .resolve();
    }

    private changeLanguageRouter = (params, query: string) => {
        i18n.instance.changeLanguage(params.lng, () => {
            sislogUserGlobals.language = params.lng;
            document.querySelector("html").lang = params.lng;
            const queryParams = getObjectFromUrl(query);
            let destination = "#!/dashboard";
            if (queryParams != null && queryParams.destination != null) {
                destination = queryParams.destination;
            }

            this.router.navigate(destination);
        });
    }

    public logout = () => {
        // for now let's get the first registered app
        const url = appModuleRegistry.apps[0].baseUrl;
        fetch(url + "/logout", {
            credentials: "include",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
            },
        });
        sislogUserGlobals.reset();
        IndexHeader.instance.reset();
        LocalStorage.clearUser();
        i18n.instance.changeLanguage(sislogUserGlobals.language, () => {
            this.router.navigate("/login?destination=/dashboard");
        });
    }

    private loginRouter = (params, query: string) => {
        const queryParams = getObjectFromUrl(query);
        let destination: any;
        if (queryParams != null && queryParams.destination != null) {
            destination = queryParams.destination;
        }
        const loginPageDefinition = {
            componentType: "fullPage",
            id: "sislog-main-login-page",
            pageTitle: "SISLOG Login",
            children: [
                {
                    componentType: "text",
                    id: "sislog-main-dashboard",
                    labelKey: "&nbsp;",
                },
                {
                    componentType: "login",
                    id: "sislog-main-login",
                    destination,
                },
            ],
        };
        Renderer.instance().fullComponentRender(loginPageDefinition, document.querySelector("body"));
    }

    private expiredPasswordRouter = (params, query: string) => {
        const queryParams = getObjectFromUrl(query);
        let destination: any;
        if (queryParams != null && queryParams.destination != null) {
            destination = queryParams.destination;
        }
        const expiredPasswordPageDefinition = {
            componentType: "fullPage",
            id: "sislog-main-expiredPassword-page",
            pageTitle: "SISLOG Password",
            children: [
                {
                    componentType: "text",
                    id: "sislog-main-dashboard",
                    labelKey: "&nbsp;",
                },
                {
                    componentType: "expiredPassword",
                    id: "sislog-main-expiredPassword",
                    destination,
                },
            ],
        };
        Renderer.instance().fullComponentRender(expiredPasswordPageDefinition, document.querySelector("body"));
    }

    private dashboardRouter = (params, query: string) => {
        const resolved = this.router.lastRouteResolved();

        if (!this.isSessionSet()) {
            this.router.navigate("/login?destination=" + resolved.url);
        } else {
            const dashboardPageDefinition = {
                host: "#main-page__content-holder",
                container: "pages/index/index.json",
                componentType: "embeddedPage",
                id: "sislog-main-dashboard-page",
                pageTitle: "SISLOG Main Dashboard",
                children: [
                    {
                        componentType: "mainDashboard",
                        id: "sislog-main-dashboard",
                    },
                ],
            };
            Renderer.instance().fullComponentRender(dashboardPageDefinition, document.querySelector("body"));
        }
    }

    private userDetailsRouter = () => {
        appModuleRegistry.currentApp = null;
        sislogUserGlobals.appEnvironment = {};
        IndexHeader.instance.updateHeaderForApp("dashboard");
        const url = appModuleRegistry.apps[0].baseUrl;
        this.loadBody(url + "main/user-details/" + LocalStorage.getUid() + ".json");
    }
    private mainRouter = (params) => {
        appModuleRegistry.currentApp = null;
        sislogUserGlobals.appEnvironment = {};
        IndexHeader.instance.updateHeaderForApp("dashboard");
        const url = appModuleRegistry.apps[0].baseUrl;
        this.loadBody(url + "main/" + params.page + ".json");
    }

    private createLegacyRoute(module: AppModule) {
        if (module.legacyUrl == null) {
            return;
        }
        let url = "/l/" + module.prefix + "/";
        for (const variable of module.context) {
            url += ":" + variable + "/";
        }
        url += ":page";
        this.router.on(
            url,
            (params, query) => {
                this.restorePrototypeJs();
                const page = params.page;
                delete params.page;
                sislogUserGlobals.appEnvironment = params;
                appModuleRegistry.currentApp = module;
                const urlParams = sislogUserGlobals.stringify();
                this.loadLegacyContainer(
                    module.legacyUrl + "/legacy-resources?" + urlParams,
                    this.legacyCallbackFactory(module, page, urlParams, query),
                );

                IndexHeader.instance.updateHeaderForApp(module.prefix);
            })
            .resolve();
    }

    private legacyCallbackFactory(
        module: AppModule,
        page: string,
        urlParams: string,
        query: string) {
        this.legacyCallbackReference = () => {
            const splitted = page.split("?");
            let legacyParams = "";
            if (splitted.length > 1) {
                legacyParams = "&" + splitted[1];
            }
            const url = module.legacyUrl
                + splitted[0].replace(/\./g, "/") + ".do?"
                + urlParams
                + "&"
                + query
                + legacyParams;
            this.loadLegacy(url);
        };
        return this.legacyCallbackReference;
    }

    public createAppRoute(module: AppModule) {
        this.createS6Route(module);
        this.createLegacyRoute(module);
    }

    public createS6Route(module: AppModule) {
        let url = "/" + module.prefix + "/";
        for (const variable of module.context) {
            url += ":" + variable + "/";
        }
        url += ":page";
        this.router.on(
            url,
            (params, query) => {
                this.cleanupPrototypeJs();
                const page = params.page;
                delete params.page;
                sislogUserGlobals.appEnvironment = params;
                appModuleRegistry.currentApp = module;
                const urlParams = sislogUserGlobals.dash();
                this.loadBody(module.baseUrl + page + "/main.json?" + urlParams + "&" + query);

                // TODO check that we have the menu for this module
                IndexHeader.instance.updateHeaderForApp(module.prefix);
            });
    }

    public registerNotFound() {
        this.router.notFound(() => {
            this.changeLoadingToError();
            message.show({
                level: "error",
                key: "error.no_route_found",
            });
        })
        this.router.resolve();
    }

    public navigate(url: string): void {
        this.router.navigate(url);
    }

    public pause() {
        this.router.pause();
    }

    public resume() {
        this.router.resume();
    }
    private isSessionSet(): boolean {
        return this.getCookie("S6SessionSet") != null;
    }

    private getCookie = (name) => {
        const match = document.cookie.match(new RegExp("(^| )" + name + window.location.port + "=([^;]+)"));
        if (match) { return match[2]; }
    }

    private loadLegacyContainer(url: RequestInfo, callback: EventListenerOrEventListenerObject) {
        Cursor.begin();
        fetch(url, {
            signal: this.signal,
            credentials: "include",
        }).then((response) => {
            if (response.status === 401 || response.status === 404) {
                throw response.status;
            }
            if (!response.ok) {
                throw Error(response.statusText);
            }
            return response.text();
        }).then((text) => {
            Router.appStartupStatus.run(() => {
                // Listen for the event.
                window.addEventListener("s5-base-legacy-scripts-loaded", callback);
                if (document.querySelector(".c-embedded-page") == null) {
                    this.loadLegacyAndMainContainer(text);
                } else {
                    this.loadLegacyInContainer(text);
                }
            });
        });
    }

    private loadLegacyAndMainContainer(text: string): void {
        const router = this;
        (async () => {
            await
                Renderer.instance().fullRender({
                    definition: {
                        host: "#main-page__content-holder",
                        container: "pages/index/index.json",
                        componentType: "embeddedPage",
                    } as any,
                }, document.querySelector("body"));
            router.loadLegacyInContainer(text);
        })();
    }

    private loadLegacyInContainer(text: string) {
        const ep = document.querySelector(".c-embedded-page") as HTMLElement;
        ep.innerHTML = text;
        this.runScripts(ep);
        Cursor.end();
    }

    private runScripts($container: HTMLElement) {
        const runScriptTypes = [
            "text/javascript",
        ];
        // get scripts tags from a node
        const $scripts = $container.querySelectorAll("script");
        let typeAttr: string;

        [].forEach.call($scripts, ($script) => {
            typeAttr = $script.getAttribute("type");

            // only run script tags without the type attribute
            // or with a javascript mime attribute value
            if (!typeAttr || runScriptTypes.indexOf(typeAttr) !== -1) {
                eval($script.text);
            }
        });
    }

    private loadLegacy(url: string) {
        window.removeEventListener("s5-base-legacy-scripts-loaded", this.legacyCallbackReference);
        (window as any).ajaxUpdateRequest(url, "", "contenido-ajax", this.moveBreadcrumbUp);
    }

    private moveBreadcrumbUp() {
        const ar = document.querySelector(".contenido-ajax .accesoRapido") ?? document.querySelector("#contenido-ajax .accesoRapido");
        if (ar != null) {
            ar.remove();
            document.querySelector(".c-breadcrumb").innerHTML = "";
            document.querySelector(".c-breadcrumb").append(ar);
        }
    }

    private loadBody = (url: RequestInfo, successCallback?) => {
        Cursor.begin();
        $(".c-embedded-page").fadeOut(500);

        fetch(url, {
            signal: this.signal,
            credentials: "include",
        })
            .then((response) => {
                if ([401, 403, 404, 504].includes(response.status)) {
                    throw response.status;
                }
                if (!response.ok) {
                    throw response;
                }
                return response.json();
            }).then((json) => {
                Router.appStartupStatus.run(() => {
                    const promise = Renderer.instance().asyncFullComponentRender(json, document.querySelector("body"));
                    promise.then(() => {
                        if (successCallback != null) {
                            successCallback();
                        }
                    });
                });
            }).catch((ex) => {
                if (ex === 401) {
                    const resolved = this.router.lastRouteResolved();
                    this.router.navigate("/login?destination=" + resolved.url);
                } else if (ex === 404) {
                    this.changeLoadingToError();
                    message.show({
                        level: "error",
                        key: "error.not_found",
                    });
                } else if (ex === 403) {
                    this.changeLoadingToError();
                    message.show({
                        level: "error",
                        key: "error.not_authorized",
                    });
                } else if (ex === 504) {
                    this.changeLoadingToError();
                    message.show({
                        level: "error",
                        key: "Backend application not avaliable",
                    });
                } else if (ex.message != null) {
                    if (ex.name === "AbortError") {
                        console.log("Fetch aborted", url);
                    } else {
                        this.changeLoadingToError();
                        message.show({
                            level: "error",
                            key: "Ops, something is wrong!!\n" + ex.message + "\n" + url,
                        });
                        console.error(ex);
                    }
                } else if (!Number.isInteger(ex)) {
                    this.changeLoadingToError();
                    (ex as Response).json().then((response) => {
                        $.each(response.messages, (key, val) => {
                            message.show(val);
                        });
                    }).catch((e) => {
                        message.show({
                            level: "error",
                            key: "Ops, something is wrong!!",
                        });
                        console.error(e);
                    });
                } else {
                    this.changeLoadingToError();
                    message.show({
                        level: "error",
                        key: "error.not_available",
                    });
                    console.error("failed", ex);
                }
            }).finally(() => {
                Cursor.end();
            });
    }

    private changeLoadingToError() {
        // document.getElementById("loading").children[0].remove();
        if (document.getElementById("loading") == null) {
            return;
        }
        const element = document.createElement("div");
        element.style.color = "#B94A48";
        element.style.fontSize = "25vmin";
        element.style.fontWeight = "bold";
        element.style.textAlign = "center";
        element.innerText = "!";
        document.getElementById("loading").children[0].replaceWith(element);
    }

    private createJsonRoute(router: Navigo, url: string) {
        router.on(
            url,
            () => {
                this.loadBody("pages/" + url + ".json");
            },
            {
                before: (done, params) => {
                    done();
                    this.cleanupPrototypeJs();
                    IndexHeader.instance.updateHeaderForApp("main");
                },
            },
        );
    }
    private cleanupPrototypeJs() {
        // We remove some extensions that we now that cause problems
        this.prototypeHtmlElementFunctions["hide"] = HTMLElement.prototype["hide"];
        delete HTMLElement.prototype["hide"];
    }
    private restorePrototypeJs() {
        // We remove some extensions that we now that cause problems
        HTMLElement.prototype["hide"] = this.prototypeHtmlElementFunctions["hide"];
    }
}

function getObjectFromUrl(query: string): any {
    const result = {};
    query.split("&").forEach((part) => {
        const item = part.split("=");
        result[item[0]] = decodeURIComponent(item[1]);
    });
    return result;
}
